import { z } from "zod/v4"

/**
 * Organization role schema based on swagger.json
 */
export const organizationRoleSchema = z.union([
  z.literal("admin"),
  z.literal("member"),
])

export type TOrganizationRole = z.infer<typeof organizationRoleSchema>

/**
 * Base organization schema
 */
export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  avatarUrl: z.string().nullable(),
  ownerId: z.string(),
  createdAt: z.string().datetime(),
})

export type TOrganization = z.infer<typeof organizationSchema>

/**
 * Organization with membership role (used in list endpoint)
 */
export const organizationWithRoleSchema = organizationSchema.extend({
  role: organizationRoleSchema,
})

export type TOrganizationWithRole = z.infer<typeof organizationWithRoleSchema>

/**
 * Membership schema (includes organization + role)
 */
export const membershipSchema = organizationSchema.extend({
  role: organizationRoleSchema,
})

export type TMembership = z.infer<typeof membershipSchema>

/**
 * API Response schemas
 */
export const createOrganizationResponseSchema = z.object({
  organizationId: z.string(),
})

export const listOrganizationsResponseSchema = z.object({
  organizations: z.array(organizationWithRoleSchema),
})

export const getOrganizationResponseSchema = z.object({
  organization: organizationSchema,
})

export const getMembershipResponseSchema = z.object({
  membership: membershipSchema,
})

export const updateOrganizationResponseSchema = z.object({
  organization: organizationSchema,
})

export type TCreateOrganizationResponse = z.infer<
  typeof createOrganizationResponseSchema
>
export type TListOrganizationsResponse = z.infer<
  typeof listOrganizationsResponseSchema
>
export type TGetOrganizationResponse = z.infer<
  typeof getOrganizationResponseSchema
>
export type TGetMembershipResponse = z.infer<typeof getMembershipResponseSchema>
export type TUpdateOrganizationResponse = z.infer<
  typeof updateOrganizationResponseSchema
>

/**
 * Member user schema (user details within a member object)
 */
export const memberUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  avatarUrl: z.string().nullable(),
})

export type TMemberUser = z.infer<typeof memberUserSchema>

/**
 * Member schema (includes membership id, role, and user details)
 */
export const memberSchema = z.object({
  id: z.string(),
  role: organizationRoleSchema,
  user: memberUserSchema,
})

export type TMember = z.infer<typeof memberSchema>

/**
 * List members response schema
 */
export const listMembersResponseSchema = z.object({
  members: z.array(memberSchema),
})

export type TListMembersResponse = z.infer<typeof listMembersResponseSchema>
